<template>
  <v-container fluid>
    <v-row>
      <v-col
        cols="12"
        sm="auto"
        align-self="center"
      >
        <div class="primary--text text-h5 my-2">
          {{ $t('employee.title') }}
        </div>
      </v-col>
      <v-spacer />
      <v-col
        cols="12"
        sm="6"
        lg="3"
      >
        <v-text-field
          v-model="search"
          :loading="loading || searchLoading"
          :disabled="loading || searchLoading"
          outlined
          dense
          :label="$t('common.search')"
          hide-details
          class="elevation-2"
          @change="onSearch"
        />
      </v-col>
    </v-row>
    <v-row v-if="isDownloadReward && !loading">
      <v-col
        cols="12"
        md="6"
        offset-md="6"
        align-self="center"
      >
        <button
          v-ripple
          class="materialDesignButton px-6 py-2"
          @click="$refs.downloadButton.click()"
        >
          {{ $t('employee.download_award') }}
        </button>
        <a
          ref="downloadButton"
          href="/api/employees/quats-programs/bonus-purchase/download"
          style="display: none"
        />
      </v-col>
    </v-row>
    <v-row v-if="errorState">
      <v-col cols="12">
        <v-alert
          v-model="errorState"
          dense
          type="error"
          close-icon="mdi-close-circle-outline"
          :dismissible="true"
        >
          {{ error }}
        </v-alert>
      </v-col>
    </v-row>
    <v-row align="stretch">
      <v-col cols="12">
        <ProgramListWidget
          :programs="programsForWidget"
          :loading="loading"
          :company="companyRelated"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-data-table
          :items="items"
          :headers="headers"
          class="elevation-2"
          :loading="loading || searchLoading"
          :sort-by.sync="option.sortBy"
          :sort-desc.sync="option.sortDesc"
          :items-per-page.sync="option.itemsPerPage"
          :page.sync="option.page"
          :server-items-length="totalItems"
          :footer-props="{
            'items-per-page-options': [5, 10, 25, 50],
            'items-per-page-text': $t('employee.pagination_text')
          }"
        >
          <template #[`item.visible_material`]="{item}">
            <div class="pt-1">
              <v-chip
                v-for="(data, index) in item.visible_material"
                :key="`vtm-${index}`"
                outlined
                color="primary"
                class="mr-1 mb-1"
              >
                {{ data.title }}
              </v-chip>
            </div>
          </template>
          <template #[`item.active_flg`]="{item}">
            <v-row
              justify="center"
              align="center"
            >
              <v-switch
                v-model="item.active_flg"
                :class="$vuetify.breakpoint.xs ? 'mr-4' : 'ml-2'"
                :disabled="(companyRelated.max - companyRelated.active === 0) && (item.active_flg === false)"
                @click="toggleActiveStatus(item.customer_id, item.active_flg)"
              />
            </v-row>
          </template>
          <template #[`item.action`]="{item}">
            <v-btn
              color="primary white--text"
              outlined
              @click="detailInfo(item.customer_id)"
            >
              {{ $t('employee.detail') }}
            </v-btn>
          </template>
          <template #footer.page-text>
            <span>{{ option.page }} / {{ totalPages }}</span>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
    <v-row>
      <v-dialog
        v-model="createEmployeeDialog"
        persistent
        :width="$vuetify.breakpoint.lgAndUp ? '60%' : $vuetify.breakpoint.xs ? '95%' : '80%'"
      >
        <template #activator="{on, attrs}">
          <v-col
            cols="12"
            class="text-center"
          >
            <v-btn
              :loading="loading"
              color="primary"
              class="elevation-2"
              :small="$vuetify.breakpoint.xs"
              v-bind="attrs"
              v-on="on"
            >
              {{ $t('employee.create_employee') }}
              <v-icon
                right
                dark
              >
                mdi-account-plus
              </v-icon>
            </v-btn>
          </v-col>
        </template>
        <v-card style="border-width: 0">
          <v-card-title class="primary white--text">
            {{ $t('employee.create_employee_marc_account') }}
            <v-spacer v-if="$vuetify.breakpoint.mdAndUp" />
            <v-btn
              v-if="$vuetify.breakpoint.mdAndUp"
              class="text-caption"
              color="white"
              outlined
              small
              @click="switchCreateMode"
            >
              {{ uploadCSV ? $t('employee.individual_creation') : $t('employee.create_with_csv') }}
            </v-btn>
          </v-card-title>
          <v-card-text
            v-if="$vuetify.breakpoint.smAndDown"
            class="pt-4 pb-0"
          >
            <v-btn
              color="primary"
              block
              small
              outlined
              @click="switchCreateMode"
            >
              {{ uploadCSV ? $t('employee.individual_creation') : $t('employee.create_with_csv_shortened') }}
            </v-btn>
          </v-card-text>
          <v-card-text
            v-if="!uploadCSV"
            class="py-3"
          >
            <v-form ref="individual">
              <v-row
                align="center"
                class="pt-4"
              >
                <v-col
                  cols="12"
                  md="6"
                  class="py-0"
                >
                  <v-row no-gutters>
                    <v-col cols="12">
                      <span>{{ $t('employee.surname') }}</span>
                      <span class="error--text">{{ ' *' }}</span>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        ref="input-0"
                        v-model.trim="employee.surname"
                        :rules="requiredRule"
                        type="text"
                        outlined
                        dense
                        @focus="currentIndex = 0"
                        @keydown.enter="focusNext"
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                  class="py-0"
                >
                  <v-row no-gutters>
                    <v-col cols="12">
                      <span>{{ $t('employee.firstname') }}</span>
                      <span class="error--text">{{ ' *' }}</span>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        ref="input-1"
                        v-model.trim="employee.name"
                        :rules="requiredRule"
                        type="text"
                        outlined
                        dense
                        @focus="currentIndex = 1"
                        @keydown.enter="focusNext"
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                  class="py-0"
                >
                  <v-row no-gutters>
                    <v-col cols="12">
                      <span>{{ $t('employee.surname_kana') }}</span>
                      <span class="error--text">{{ ' *' }}</span>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        ref="input-2"
                        v-model.trim="employee.surnameKana"
                        :rules="requiredRule"
                        type="text"
                        outlined
                        dense
                        @focus="currentIndex = 2"
                        @keydown.enter="focusNext"
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                  class="py-0"
                >
                  <v-row no-gutters>
                    <v-col cols="12">
                      <span>{{ $t('employee.firstname_kana') }}</span>
                      <span class="error--text">{{ ' *' }}</span>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        ref="input-3"
                        v-model.trim="employee.nameKana"
                        :rules="requiredRule"
                        type="text"
                        outlined
                        dense
                        @focus="currentIndex = 3"
                        @keydown.enter="focusNext"
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                  class="py-0"
                >
                  <v-row
                    align="center"
                    justify="center"
                  >
                    <v-col cols="12">
                      <span>{{ $t('employee.dob_title') }}</span>
                      <span class="error--text">{{ ' *' }}</span>
                    </v-col>
                    <v-col
                      cols="4"
                      class="pr-1 pt-1 pb-0"
                    >
                      <v-autocomplete
                        ref="input-4"
                        v-model="employee.dob_year"
                        :append-icon="$vuetify.breakpoint.mdAndUp ? '$dropdown' : ''"
                        :items="years"
                        class="inputDateOfBirth"
                        :label="$t('employee.dob_year')"
                        outlined
                        dense
                        type="number"
                        hide-details
                        @focus="currentIndex = 4"
                        @keydown.enter="focusNext"
                      />
                    </v-col>
                    <v-col
                      cols="4"
                      class="px-1 pt-1 pb-0"
                    >
                      <v-autocomplete
                        ref="input-5"
                        v-model="employee.dob_month"
                        class="inputDateOfBirth"
                        :append-icon="$vuetify.breakpoint.mdAndUp ? '$dropdown' : ''"
                        :label="$t('employee.dob_month')"
                        outlined
                        dense
                        type="number"
                        :items="months"
                        hide-details
                        @focus="currentIndex = 5"
                        @keydown.enter="focusNext"
                      />
                    </v-col>
                    <v-col
                      cols="4"
                      class="pl-1 pt-1 pb-0"
                    >
                      <v-autocomplete
                        ref="input-6"
                        v-model="employee.dob_day"
                        :items="days"
                        :append-icon="$vuetify.breakpoint.mdAndUp ? '$dropdown' : ''"
                        class="inputDateOfBirth"
                        :label="$t('employee.dob_day')"
                        outlined
                        dense
                        type="number"
                        hide-details
                        @focus="currentIndex = 6"
                        @keydown.enter="focusNext"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      class="pb-5 pt-1"
                    >
                      <div class="error--text text-body-2 static-text-height">
                        <span v-if="dobErrorString">{{ dobErrorString }}</span>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                  class="py-0"
                >
                  <v-row
                    align="center"
                  >
                    <v-col cols="12">
                      <span>{{ $t('employee.phone_number') }}</span>
                      <span class="error--text">{{ ' *' }}</span>
                    </v-col>
                    <v-col
                      cols="4"
                      class="pt-1 pr-1 pb-0"
                    >
                      <v-text-field
                        ref="input-7"
                        v-model="employee.phoneNumberFirst"
                        class="inputDateOfBirth"
                        outlined
                        dense
                        maxlength="4"
                        hide-details
                        @focus="currentIndex = 7"
                        @keydown.enter="focusNext"
                      />
                    </v-col>
                    <v-col
                      cols="4"
                      class="pt-1 px-1 pb-0"
                    >
                      <v-text-field
                        ref="input-8"
                        v-model="employee.phoneNumberSecond"
                        class="inputDateOfBirth"
                        outlined
                        dense
                        maxlength="4"
                        hide-details
                        @focus="currentIndex = 8"
                        @keydown.enter="focusNext"
                      />
                    </v-col>
                    <v-col
                      cols="4"
                      class="pt-1 pl-1 pb-0"
                    >
                      <v-text-field
                        ref="input-9"
                        v-model="employee.phoneNumberThird"
                        class="inputDateOfBirth"
                        outlined
                        dense
                        maxlength="9"
                        hide-details
                        @focus="currentIndex = 9"
                        @keydown.enter="focusNext"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      class="pb-5 pt-1"
                    >
                      <div
                        class="error--text text-body-2 static-text-height"
                      >
                        <span v-if="phoneNumberErrorString">{{ phoneNumberErrorString }}</span>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                  class="py-0"
                >
                  <v-row no-gutters>
                    <v-col cols="12">
                      <span>{{ $t('employee.sex') }}</span>
                      <span class="error--text">{{ ' *' }}</span>
                    </v-col>
                    <v-col cols="12">
                      <v-select
                        ref="input-10"
                        v-model="employee.sex"
                        :items="$i18n.locale === 'jp' ? genders_jp : genders_en"
                        :rules="requiredRule"
                        dense
                        outlined
                        @focus="currentIndex = 10"
                        @click="currentIndex = 10"
                        @keydown.space="currentIndex = 10"
                        @keydown.enter="currentIndex = 10"
                        @change="optionChanged"
                      />
                    </v-col>
                  </v-row>
                </v-col>
                <v-col
                  cols="12"
                  md="6"
                  class="py-0"
                >
                  <v-row no-gutters>
                    <v-col cols="12">
                      <span>{{ $t('employee.mail_address') }}</span>
                      <span class="error--text">{{ ' *' }}</span>
                    </v-col>
                    <v-col cols="12">
                      <v-text-field
                        ref="input-11"
                        v-model.trim="employee.mailAddress"
                        :rules="emailRule"
                        type="text"
                        outlined
                        dense
                        validate-on-blur
                        @keydown.enter="createEmployee"
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-text
            v-else
            class="py-4"
          >
            <upload-c-s-v
              :file-uploaded="uploadStatus"
              :no-file="noCSV"
              @dataUploaded="getCSVData($event)"
              @dataCleared="resetUploadStatus"
              @headerMatch="isHeaderMatch = $event"
            />
          </v-card-text>
          <v-card-text v-if="error">
            <v-alert
              v-if="error"
              dense
              type="error"
              close-icon="mdi-close-circle-outline"
              :dismissible="true"
            >
              {{ error }}
            </v-alert>
          </v-card-text>
          <v-card-actions
            v-if="$vuetify.breakpoint.smAndUp"
            class="pb-4 px-6"
          >
            <v-btn
              v-if="!uploadCSV"
              :loading="isRegistering"
              color="primary"
              outlined
              @click="createEmployee"
            >
              {{ $t('employee.apply') }}
            </v-btn>
            <v-btn
              v-if="uploadCSV"
              :class="isHeaderMatch ? '' : 'custom-btn-disable'"
              :loading="isRegistering"
              color="primary"
              outlined
              :disabled="!isHeaderMatch"
              @click="createEmployeeWithCSV"
            >
              {{ $t('employee.apply') }}
            </v-btn>
            <v-btn
              color="warning"
              outlined
              @click="cancelCreateEmployee"
            >
              {{ $t('common.cancel') }}
            </v-btn>
            <span
              class="pl-2 text-justify"
              style="white-space: pre-line; font-size: small;"
              v-text="$t('employee.dialog_note')"
            />
          </v-card-actions>
          <v-card-actions
            v-if="$vuetify.breakpoint.xs && !uploadCSV"
            class="pt-0 pb-2 px-6"
          >
            <v-btn
              :loading="isRegistering"
              color="primary"
              outlined
              block
              @click="createEmployee"
            >
              {{ $t('employee.apply') }}
            </v-btn>
          </v-card-actions>
          <v-card-actions
            v-if="$vuetify.breakpoint.xs && uploadCSV"
            class="pt-0 pb-2 px-6"
          >
            <v-btn
              :loading="isRegistering"
              color="primary"
              outlined
              block
              :disabled="isHeaderMatch"
              @click="createEmployeeWithCSV"
            >
              {{ $t('employee.apply') }}
            </v-btn>
          </v-card-actions>
          <v-card-actions
            v-if="$vuetify.breakpoint.xs"
            class="pt-0 pb-4 px-6"
          >
            <v-btn
              color="warning"
              outlined
              block
              @click="cancelCreateEmployee"
            >
              {{ $t('common.cancel') }}
            </v-btn>
          </v-card-actions>
          <div
            v-if="$vuetify.breakpoint.xs"
            class="px-6 text-justify pb-6"
            style="white-space: pre-line; font-size: small;"
            v-text="$t('employee.dialog_note')"
          />
        </v-card>
      </v-dialog>
      <v-col cols="auto">
        <div
          style="white-space: pre-line; font-size: small;"
          v-text="$t('employee.note')"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import UploadCSV from '../../../components/UploadCSV'
import ProgramListWidget from '../../../components/ProgramListWidget'
import QUATSError from '../../../plugins/error'

export default {
  components: { ProgramListWidget, UploadCSV },
  data() {
    return{
      employee: {
        surname: '',
        name: '',
        surnameKana: '',
        nameKana: '',
        dob: '',
        dob_year: new Date().getFullYear().toString(),
        dob_month: '01',
        dob_day: '01',
        sex: '',
        mailAddress: '',
        phoneNumber: '',
        phoneNumberFirst: '',
        phoneNumberSecond: '',
        phoneNumberThird:''
      },
      isHeaderMatch: false,
      createEmployeeDialog: false,
      search: '',
      totalItems: 0,
      option: {
        itemsPerPage: 50,
        page: 1,
        sortBy: 'name_kana',
        sortDesc: false,
      },
      totalPages: 1,
      loading: false,
      items: [],
      uploadCSV: false,
      csvEmployee: [],
      company_name: '',
      isRegistering: false,
      error: null,
      activePicker: null,
      menu: false,
      genders_en: ['No answer', 'Male', 'Female'],
      genders_jp: ['未回答', '男性' , '女性'],
      currentIndex: 0,
      companyRelated: {},
      currentCompanyPrograms: [],
      allPrograms: [],
      programsForWidget: [],
      years: [],
      months: [],
      days: [],
      translatedPostData: [],
      translationBuffer: {},
      uploadStatus: false,
      canDownload: false,
      noCSV: false,
      searchLoading: false,
      errorState: false,
      phoneIsFilledOnce: false
    }
  },

  computed: {
    /***
     * @desc helper function to reactively return if the current user can download the award
     * @returns {boolean}
     */
    isDownloadReward() {
      return this.canDownload
    },

    /**
     * @desc Generate Email Rule
     * */
    emailRule () {
      const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return [
        v => !!v?.trim() || this.$t('common.required'),
        v => pattern.test(v?.trim()) || this.$t('common.email_invalid')
      ]
    },

    /**
     * @desc Default Required Rule
     * */
    requiredRule() {
      return [
        v => !!v?.trim() || this.$t('common.required')
      ]
    },

    /**
     * @desc to join phone number and validate
     * */
    phoneNumberErrorString () {
      if (this.employee.phoneNumberFirst && this.employee.phoneNumberSecond && this.employee.phoneNumberThird) {
        const pattern = /^([0-9]{1,4})-([0-9]{1,4})-([0-9]{1,9})$/
        const phoneNumberWithHyphen = `${this.employee.phoneNumberFirst}-${this.employee.phoneNumberSecond}-${this.employee.phoneNumberThird}`
        if (!pattern.test(phoneNumberWithHyphen)) {
          return this.$t('common.phone_invalid')
        }
        if(phoneNumberWithHyphen.length > 15) {
          return this.$t('common.phone_invalid')
        }
      }
      if (!this.employee.phoneNumberFirst && !this.employee.phoneNumberSecond && !this.employee.phoneNumberThird) {
        if (this.phoneIsFilledOnce) {
          return this.$t('common.required')
        }
      }
      if (this.employee.phoneNumberFirst || this.employee.phoneNumberSecond || this.employee.phoneNumberThird) {
        const numeric = /^[0-9]*$/
        if (!numeric.test(this.employee.phoneNumberFirst) || !numeric.test(this.employee.phoneNumberSecond) || !numeric.test(this.employee.phoneNumberThird)) {
          return this.$t('common.phone_invalid')
        }
      }
      return null
    },

    /**
     * @desc to check if the phone number can be submitted
     * */
    phoneNumberCanBeSubmitted () {
      if (this.employee.phoneNumberFirst && this.employee.phoneNumberSecond && this.employee.phoneNumberThird) {
        if (!this.phoneNumberErrorString) {
          return true
        }
      }
      return false
    },

    /**
     * @desc General phone number rules for Japanese format
     * */
    phoneNumberRules() {
      const pattern = /^[0-9]{1,9}$/
      return [
        v => !!v?.trim() || this.$t('common.required'),
        v => pattern.test(v?.trim()) || this.$t('common.invalid')
      ]
    },

    /**
     * @desc General DOB rule for DOB String, checks if the date exist, and is in the past
     * */
    dobErrorString () {
      if (this.employee.dob_day && this.employee.dob_month && this.employee.dob_year) {
        const dob = new Date(`${this.employee.dob_year}-${this.employee.dob_month}-${this.employee.dob_day}`)
        if (!(dob.getFullYear() == this.employee.dob_year && dob.getMonth() + 1 == this.employee.dob_month && dob.getDate() == this.employee.dob_day)) {
          return this.$t('common.dob_invalid')
        }
        const inputTimestamp = dob.setHours(0,0,0,0)
        const currentTimestamp = new Date().setHours(0,0,0,0)
        if (inputTimestamp >= currentTimestamp) {
          return this.$t('common.dob_invalid')
        }
      }
      if (!this.employee.dob_day && !this.employee.dob_month && !this.employee.dob_year) {
        return this.$t('common.required')
      }
      return null
    },

    /**
     * @desc to check if the date of birth can be submitted
     * */
    dobCanBeSubmitted () {
      if (this.employee.dob_day && this.employee.dob_month && this.employee.dob_year) {
        if (!this.dobErrorString) {
          return true
        }
      }
      return false
    },

    /**
     * @desc Generate Date of Birth Rule
     * */
    yearDOBRule() {
      return [
        v => !!v || this.$t('common.required'),
        v => (v >= (new Date().getFullYear() - 120) && v <= new Date().getFullYear()) || this.$t('common.invalid')
      ]
    },

    /**
     * @desc Generate Date of Birth Rule
     * */
    monthDOBRule() {
      return [
        v => !!v || this.$t('common.required'),
        v => (v >= 1 && v <= 12) || this.$t('common.invalid')
      ]
    },

    /**
     * @desc Generate Date of Birth Rule
     * */
    dayDOBRule() {
      return [
        v => !!v || this.$t('common.required'),
        v => (v >= 1 && v <= 31) || this.$t('common.invalid')
      ]
    },

    searchParams() {
      let requestParam = `?page_size=${this.option.itemsPerPage}&page=${this.option.page}`
      if (this.search) {
        requestParam += `&search=${this.search}`
      }
      if (this.option.sortBy) {
        requestParam += `&sort_by=${this.option.sortBy}`
      }
      if (this.option.sortDesc) {
        requestParam += `&sort_desc=${this.option.sortDesc}`
      }
      return requestParam
    },

    /**
     * @desc set the data of v-data-table header and translate the header name based on the locale
     * @returns {[{width: string, text: VueI18n.TranslateResult, align: string, value: string, class: string},{width: string, text: VueI18n.TranslateResult, align: string, value: string, class: string},{width: string, text: VueI18n.TranslateResult, sortable: boolean, align: string, value: string, class: string},{width: string, text: VueI18n.TranslateResult, sortable: boolean, align: string, value: string, class: string},{width: string, text: VueI18n.TranslateResult, sortable: boolean, align: string, value: string, class: string}]}
     */
    headers() {
      return [
        { text: this.$t('employee.name'), align: 'start', value: 'name', width: '150px', class: 'primary white--text' },
        { text: this.$t('employee.name_kana'), align: 'start', value: 'name_kana', width: '150px', class: 'primary white--text' },
        { text: this.$t('employee.visible_teaching_material'), align: 'start', value: 'visible_material', sortable: false, width: '200px', class: 'primary white--text' },
        { text: this.$t('common.activation'), align: 'center', value: 'active_flg', sortable: false, width: '150px', class: 'primary white--text' },
        { text: this.$t('employee.action'), align: 'center', value: 'action', sortable: false, width: '100px', class: 'primary white--text' },
      ]
    }
  },
  watch: {
    uploadCSV() {
      !this.errorState || (this.errorState = true)
    },

    option: {
      deep: true,
      handler() {
        this.onSearch()
      }
    },
    'employee.phoneNumberFirst': {
      handler() {
        if (this.employee.phoneNumberFirst) {
          this.phoneIsFilledOnce = true
        }
      }
    },
    'employee.phoneNumberSecond': {
      handler() {
        if (this.employee.phoneNumberSecond) {
          this.phoneIsFilledOnce = true
        }
      }
    },
    'employee.phoneNumberThird': {
      handler() {
        if (this.employee.phoneNumberThird) {
          this.phoneIsFilledOnce = true
        }
      }
    }
  },
  mounted() {
    this.onPageRefresh()
    this.getDOBList()
  },
  methods: {
    /**
     * @desc function to generate list of year, month and date
     */
    getDOBList() {
      let year = new Date().getFullYear(), m = 1, d = 1
      const years = [], months = [], days = [], y = year - 120
      while (year >= y) {
        years.push(year.toString())
        year -= 1
      }
      while (m <= 12) {
        months.push(m.toLocaleString('en-US', {
          minimumIntegerDigits: 2,
          useGrouping: false
        }))
        m += 1
      }
      while (d <= 31) {
        days.push(d.toLocaleString('en-US', {
          minimumIntegerDigits: 2,
          useGrouping: false
        }))
        d += 1
      }
      this.years = years
      this.months = months
      this.days = days
    },

    /**
     * @desc to assign csv data
     * */
    getCSVData (csvData) {
      this.csvEmployee = csvData
    },

    /**
     * @desc to reset upload status once csv component has been cleared
     */
    resetUploadStatus () {
      this.uploadStatus = false
    },

    /**
     * @desc Handler to handle when create employee with CSV
     * */
    createEmployeeWithCSV () {
      if(this.csvEmployee.length > 0) {
        this.noCSV=false
        this.isRegistering = true
        this.translationBuffer = {}
        this.csvEmployee.forEach((x) => {
          this.translationBuffer['surname'] = x['姓']
          this.translationBuffer['name'] = x['名']
          this.translationBuffer['surnameKana'] = x['姓（カナ）']
          this.translationBuffer['nameKana'] = x['名（カナ）']
          this.translationBuffer['dob'] = x['生年月日']
          this.translationBuffer['sex'] = x['性別']
          this.translationBuffer['phoneNumber'] = x['電話番号']
          this.translationBuffer['mailAddress'] = x['メールアドレス']
          this.translatedPostData.push(this.translationBuffer)
          this.translationBuffer = {}
        })
        const postData = { company_name: this.companyRelated.name, employee_list: this.translatedPostData }
        window.axios.post('/api/employees/employees', postData)
          .then(() => {
            this.createEmployeeDialog = false
            this.translatedPostData = []
            this.uploadStatus = true
            this.errorState = false
          }).catch(err => {
            this.error = new QUATSError(this.$i18n, err).message
            this.errorState = true
          }).finally(() => {
            this.isRegistering = false
          })
      } else {
        this.noCSV=true
      }
    },

    /**
     * @desc this function will be called when the table is loaded, refreshed or search criteria is changed
     */
    onSearch() {
      this.searchLoading = true
      const promises = [
        window.axios.get('/api/employees/quats-programs'),
        window.axios.get(`/api/employees/employees${this.searchParams}`)
      ]
      Promise.all(promises)
        .then(result => {
          this.onSearchResult(result[0].data.data.data, result[1].data.data.data)
          this.$store.commit('snackbar/successSnackbar', [this.$t('employee.search_success_message')])
        }).catch(err => {
          this.error = new QUATSError(this.$i18n, err).message
          this.errorState = true
        }).finally(() => {
          this.searchLoading = false
        })
    },

    /***
     * @desc helper function to generate data after search
     * @param programs all program list
     * @param employees all employees list belong to the current account
     */
    onSearchResult(programs, employees) {
      employees.data.forEach(employee => {
        const visible_material = [];
        (employee.program_quats_permissions.group_ids).forEach(id => {
          visible_material.push(programs.find(program => program.groupId === id))
        })
        employee['visible_material'] = visible_material
      })
      this.items = employees.data
      this.totalItems = employees.total
      this.totalPages = employees.last_page
    },

    /**
     * @desc this function will redirect to detail page and show the detail information of the selected user
     */
    detailInfo(id) {
      this.$router.push({name: 'Detail', params: {id: id.toString()}})
    },

    /**
     * @desc enable or disable corporate account
     */
    toggleActiveStatus(customerId, newStatus) {
      const requestBody = {
        'customer_id': customerId,
        'active': newStatus
      }
      window.axios.patch('/api/employees/corporate-account/employee/toggle', requestBody)
        .then(res => {
          this.toggleResult = res
          if (newStatus === true) {
            this.$store.commit('snackbar/successSnackbar', [this.$t('employee.corporate_activated')])
          } else {
            this.$store.commit('snackbar/successSnackbar', [this.$t('employee.corporate_deactivated')])
          }
          this.onPageRefresh()
          this.errorState = false
        }).catch(err => {
          this.error = new QUATSError(this.$i18n, err).message
          this.errorState = true
          this.loading = false
        })
    },

    /**
     * @desc to close dialog of create employee dialog
     */
    cancelCreateEmployee() {
      if(!this.uploadCSV) {
        this.onResetData()
        this.$refs.individual.resetValidation()
        this.phoneIsFilledOnce = false
        this.createEmployeeDialog = false
      } else {
        this.noCSV = this.uploadCSV = this.isHeaderMatch = this.uploadStatus = this.createEmployeeDialog = false
      }
    },

    /**
     * @desc handler of creating employee
     */
    createEmployee() {
      if(
        this.$refs.individual.validate()
        && this.dobCanBeSubmitted
        && this.phoneNumberCanBeSubmitted
      ) {
        this.employee.phoneNumber = this.employee.phoneNumberFirst.concat('-').concat(this.employee.phoneNumberSecond).concat('-').concat(this.employee.phoneNumberThird)
        this.employee.dob = this.employee.dob_year.concat('-').concat(this.employee.dob_month).concat('-').concat(this.employee.dob_day)
        this.isRegistering = true
        const postData = { company_name: this.companyRelated.name, employee_list: [this.employee] }
        window.axios.post('/api/employees/employees', postData)
          .then(() => {
            this.$refs.individual.resetValidation()
            this.onResetData()
            this.phoneIsFilledOnce = false
            this.createEmployeeDialog = false
            this.errorState = false
          }).catch(err => {
            this.error = new QUATSError(this.$i18n, err).message
            this.errorState = true
          }).finally(() => {
            this.isRegistering = false
          })
      }
    },

    /**
     * @desc to get Parent Company information and corporate account information
     * */
    onPageRefresh() {
      this.loading = true
      const promises = [
        window.axios.get('/api/employees/parent-company'),
        window.axios.get('/api/employees/corporate-account/quota'),
        window.axios.get('/api/employees/quats-programs'),
        window.axios.get('/api/employees/quats-programs/quota'),
        window.axios.get('/api/employees/quats-programs/bonus-purchase/check-download'),
        window.axios.get(`/api/employees/employees${this.searchParams}`),
      ]
      Promise.all(promises)
        .then(result => {
          this.companyRelated = Object.assign({}, {
            id: result[0].data.data.data.id,
            name: result[0].data.data.data.name,
            name_kana: result[0].data.data.data.name_kana,
            max: result[1].data.data.data.total,
            active: result[1].data.data.data.active
          })
          this.filterDataForWidget(result[2].data.data.data, result[3].data.data.data.current)
          this.canDownload = result[4].data.data.data.status
          this.onSearchResult(result[2].data.data.data, result[5].data.data.data)
          this.errorState = false
        }).catch(err => {
          this.error = new QUATSError(this.$i18n, err).message
          this.errorState = true
        }).finally(() => {
          this.loading = false
        })
    },

    /**
     * @desc filter program of the current company to show in widget
     */
    filterDataForWidget(allPrograms, currentCompanyPrograms) {
      const programsForWidget = []
      currentCompanyPrograms.forEach((x) => {
        allPrograms.forEach((y) => {
          if (x.group_id === y.groupId) {
            x.title = y.title
          }
        })
        programsForWidget.push(x)
      })
      this.programsForWidget = programsForWidget
    },

    /**
     * @desc to reset employee form data correctly
     */
    onResetData() {
      this.employee = Object.assign({}, {
        surname: '',
        name: '',
        surnameKana: '',
        nameKana: '',
        dob: '',
        dob_year: new Date().getFullYear().toString(),
        dob_month: '01',
        dob_day: '01',
        sex: '',
        mailAddress: '',
        phoneNumber: '',
        phoneNumberFirst: '',
        phoneNumberSecond: '',
        phoneNumberThird: ''
      })
    },

    /**
     * @desc to move employee creation form focus to the next
     */
    focusNext() {
      this.currentIndex += 1
      const nextElement = this.$refs[`input-${this.currentIndex}`]
      if (nextElement) nextElement.focus()
    },

    /**
     * @desc to detect selection change and process the next function of form correctly
     */
    optionChanged() {
      if (this.employee.sex.length !== 0) {
        this.focusNext()
      }
    },

    switchCreateMode() {
      if(!this.uploadCSV) {
        this.onResetData()
        this.$refs.individual.resetValidation()
        this.phoneIsFilledOnce = false
        this.uploadCSV = !this.uploadCSV
      } else {
        this.noCSV = false
        this.uploadStatus = false
        this.isHeaderMatch = false
        this.uploadCSV = !this.uploadCSV
      }
    }
  }
}
</script>

<style scoped>

.materialDesignButton {
  color:#fff;
  border-radius:5px;
  font-family: "Noto Sans JP", sans-serif;
  font-size:1.04em;
  letter-spacing: 0.05em;
  width: 100%;
  transition: 0.3s;
  background-color: #072a67;
}

.custom-btn-disable {
  background-color: rgba(0,0,0,.12)!important;
}

.materialDesignButton:hover {
  opacity: 0.8;
}

.inputDateOfBirth >>> input[type='number'] {
  -moz-appearance: textfield;
}
.inputDateOfBirth >>> input::-webkit-outer-spin-button,
.inputDateOfBirth >>> input::-webkit-inner-spin-button {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}
.csv-btn.v-btn--outlined {
  border: thin solid #FFF !important;
}
::v-deep .theme--light.v-data-table .v-data-table-header th.sortable .v-data-table-header__icon {
  color: white;
}
.static-text-height {
  height: 1.4em;
}
</style>
